import type { OutputStatus } from '@/types';

/**
 * 即時事件型別
 * - `output:status`：輸出節點狀態推進（pending → running → done）
 * - `graph:updated`：協作者改動了圖（可觸發 refetch 或 diff）。
 *   ``source`` 區分「本地剛寫入」vs「其他 tab / Firebase 推來的」 — CoreCanvas 用這個
 *   旗標決定要不要 auto-reload（自己剛寫的不必重 load，會跟在用 state 打架）。
 *
 * R168 — `run:*` 三條事件(節點/流程執行進度)已終刪:唯一 emitter `executionService`
 * 是 R55 前的 client-side stub、零 caller,生成一律走 `POST /jobs` + Firestore listener。
 */
export type GraphUpdateSource = 'local' | 'remote';

export type RealtimeEvent =
  | { type: 'output:status'; projectId: string; nodeId: string; status: OutputStatus }
  | { type: 'graph:updated'; projectId: string; revision: number; source: GraphUpdateSource };

/**
 * RealtimeBus 介面
 * -----------------------------------------------------------------------------
 * 目前是 in-memory pub/sub(by-design client-side;graph:updated 跨 tab 用 storage
 * event);未來若改用 server push:
 *   - WebSocket(`WS /projects/:id/stream`)
 *   - Server-Sent Events
 *   - Firebase Firestore `onSnapshot`(已部分用於 Job listener)
 *
 * 三條 API:
 *   - `subscribe(projectId, handler)`:只收特定 project 的事件(CoreCanvas /
 *     useCanvasRealtimeEffects 用這個)
 *   - `subscribeAll(handler)`:收所有 project 的事件(R168 起無 caller;介面保留給
 *     未來跨 project 監聽需求)
 *   - `emit(event)`:由 canvas 寫入路徑 push(持久化後的 `graph:updated`);
 *     application code 不該直接 emit(by convention,非型別強制)
 *
 * R41.6 起為 by-design client-side service;砍 mock layer 後 implementation 收進本檔。
 */
export interface RealtimeBus {
  subscribe(projectId: string, handler: (event: RealtimeEvent) => void): () => void;
  subscribeAll(handler: (event: RealtimeEvent) => void): () => void;
  emit(event: RealtimeEvent): void;
}

/**
 * In-memory pub/sub impl:`Map<projectId, Set<handler>>`。
 *
 * - `subscribe(projectId, handler)`:加進對應 set,回 unsubscribe
 * - `emit(event)`:依 `event.projectId` 派發給所有訂閱者
 *
 * Handler 拋例外時不影響其他 handler;錯誤透過 `console.error` 浮現以便除錯。
 */
export function createRealtimeBus(): RealtimeBus {
  const handlersByProject = new Map<string, Set<(event: RealtimeEvent) => void>>();
  const allHandlers = new Set<(event: RealtimeEvent) => void>();

  const safeInvoke = (handler: (event: RealtimeEvent) => void, event: RealtimeEvent) => {
    try {
      handler(event);
    } catch (err) {
      // 一個 handler 壞不該拖垮其他訂閱者;log 出來方便偵錯
      console.error('[realtime-bus] handler threw', event, err);
    }
  };

  return {
    subscribe(projectId, handler) {
      let set = handlersByProject.get(projectId);
      if (!set) {
        set = new Set();
        handlersByProject.set(projectId, set);
      }
      set.add(handler);
      return () => {
        set?.delete(handler);
        if (set && set.size === 0) handlersByProject.delete(projectId);
      };
    },

    subscribeAll(handler) {
      allHandlers.add(handler);
      return () => {
        allHandlers.delete(handler);
      };
    },

    emit(event) {
      const set = handlersByProject.get(event.projectId);
      if (set) {
        for (const handler of set) safeInvoke(handler, event);
      }
      // `subscribeAll` 訂閱者也收一份。Set 在迭代中加 / 刪會分裂行為,先複製一份以免
      // 訂閱者在 handler 內反訂閱造成迭代器跳號。
      if (allHandlers.size > 0) {
        for (const handler of [...allHandlers]) safeInvoke(handler, event);
      }
    },
  };
}
